import { useDebounce } from "@prismicio/editor-support/React"
import {
  AnimatedElement,
  Box,
  DefaultErrorMessage,
  dialogErrorImage,
  ErrorBoundary,
  Toast
} from "@prismicio/editor-ui"
import { type ReactNode, Suspense, useState, useTransition } from "react"

import { ActionBar } from "./ActionBar"
import { CreateNewDocumentModal } from "./CreateNewDocumentDialog"
import {
  DocumentListLoadingTable,
  DocumentListTable
} from "./DocumentListTable"

interface DocumentListBodyProps {
  tab: "working" | "archived"
}

export function DocumentListBody(props: DocumentListBodyProps) {
  const { tab } = props

  const [isLoadingPage, startTransition] = useTransition()

  const [debouncedIsLoading, setDebouncedIsLoading] = useState<boolean>(false)

  const [modalOpen, setModalOpen] = useState(false)

  useDebounce(isLoadingPage, 100, () => setDebouncedIsLoading(isLoadingPage))

  return (
    <Box flexDirection="column" width="100%">
      <Box
        flexGrow={1}
        height="fit-content"
        padding={{ left: 24, top: 24, bottom: 24 }}
        flexDirection="column"
        gap={24}
      >
        <ActionBar tab={tab} setModalOpen={setModalOpen} />

        <AnimatedTable tab={tab}>
          <ErrorBoundary renderError={renderDocumentListError}>
            <Suspense fallback={<DocumentListLoadingTable />}>
              <DocumentListTable
                startTransition={startTransition}
                tab={tab}
                setModalOpen={setModalOpen}
              />
            </Suspense>
          </ErrorBoundary>
        </AnimatedTable>
      </Box>

      <LoadingToast debouncedIsLoading={debouncedIsLoading} />
      <CreateNewDocumentModal open={modalOpen} onOpenChange={setModalOpen} />
    </Box>
  )
}

interface LoadingToastProps {
  debouncedIsLoading: boolean
}

function LoadingToast(props: LoadingToastProps) {
  return (
    <Toast
      anchor={
        <Box
          position="relative"
          width="100%"
          justifyContent="center"
          bottom={72}
        />
      }
      loadingIndicator
      open={props.debouncedIsLoading}
      title="Loading more results ..."
      seconds={1}
    />
  )
}

interface AnimatedTableProps {
  tab: "working" | "archived"
  children: ReactNode
}

function AnimatedTable(props: AnimatedTableProps) {
  const { tab, children } = props

  return (
    <AnimatedElement
      animation={{ name: "slide", offset: -16 }}
      enterDuration={250}
      exitDuration={150}
    >
      <Box key={tab}>{children}</Box>
    </AnimatedElement>
  )
}

function renderDocumentListError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching documents."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}
